import { useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  motion,
  useInView,
  useScroll,
  useTransform,
  AnimatePresence,
} from "motion/react";
import { ArrowLeft } from "lucide-react";
import { workCards } from "../data/work-cards";
import { categories } from "../data/category-mapping";

/* ─── SPRING CONFIGS ─────────────────────────── */
const SPRING_SOFT = { type: "spring", stiffness: 55, damping: 14, mass: 1 };
const SPRING_CRISP = { type: "spring", stiffness: 120, damping: 18 };
const EASE_EXPO = [0.16, 1, 0.3, 1];

/* ─── HERO HEADER ────────────────────────────── */
function CategoryHero({ category }) {
  return (
    <div className="relative mb-24 md:mb-32 overflow-hidden">
      {/* Back button */}
      <BackButton />

      {/* Eyebrow */}
      <motion.div
        initial={{ opacity: 0, x: -24 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: EASE_EXPO, delay: 0.25 }}
        className="flex items-center gap-3 mb-6"
      >
        <div className="w-6 h-px bg-indigo-500/60" />
        <span
          style={{ fontFamily: "'Montserrat', sans-serif" }}
          className="text-[0.6rem] font-semibold tracking-[0.22em] uppercase text-indigo-400/70"
        >
          Technology / {category.title}
        </span>
      </motion.div>

      {/* Big title — word by word */}
      <div className="overflow-hidden mb-6">
        <motion.h1
          initial={{ y: 100, opacity: 0, skewY: 4 }}
          animate={{ y: 0, opacity: 1, skewY: 0 }}
          transition={{ duration: 1, ease: EASE_EXPO, delay: 0.35 }}
          style={{ fontFamily: "'Montserrat', sans-serif" }}
          className="text-[clamp(3rem,10vw,7.5rem)] font-black tracking-tight leading-[0.92] text-white"
        >
          {category.title}
        </motion.h1>
      </div> 

      {/* Desc + accent line */}
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE_EXPO, delay: 0.55 }}
        className="flex items-start gap-5 max-w-2xl"
      >
        <div className="w-0.5 self-stretch bg-gradient-to-b from-indigo-500 via-purple-500 to-transparent flex-shrink-0 mt-1" />
        <p
          style={{ fontFamily: "'Montserrat', sans-serif", textAlign: "justify" }}
          className="text-base md:text-lg font-light text-white/45 leading-relaxed text-justify"
        >
          {category.description}
        </p>
      </motion.div>

      {/* Divider */}
      <motion.div
        initial={{ scaleX: 0, originX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.9, ease: EASE_EXPO, delay: 0.7 }}
        className="mt-14 h-px bg-gradient-to-r from-white/10 via-indigo-500/20 to-transparent"
      />
    </div>
  );
}

/* ─── BACK BUTTON ────────────────────────────── */
function BackButton() {
  const navigate = useNavigate();
  return (
    <motion.button
      initial={{ opacity: 0, x: -16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, ease: EASE_EXPO, delay: 0.1 }}
      onClick={() => navigate("/technology")}
      whileHover="hover"
      className="group inline-flex items-center gap-2 mb-10 text-white/35 hover:text-white transition-colors duration-300"
    >
      <motion.span variants={{ hover: { x: -4 } }} transition={SPRING_CRISP}>
        <ArrowLeft size={14} />
      </motion.span>
      <span
        style={{ fontFamily: "'Montserrat', sans-serif" }}
        className="text-xs font-semibold tracking-[0.14em] uppercase"
      >
        Technology
      </span>
    </motion.button>
  );
}

/* ─── SINGLE WORK CARD ───────────────────────── */
function WorkCard({ card, index }) {
  const ref = useRef(null);
  const imgRef = useRef(null);

  const isInView = useInView(ref, { once: true, amount: 0.15 });

  /* Parallax on image */
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  /* Direction: odd cards flip layout */
  const isEven = index % 2 === 0;

  /* Per-index accent colours cycling through indigo → purple → pink */
  const accents = [
    { rgb: "99,102,241", hex: "#6366f1" },
    { rgb: "168,85,247", hex: "#a855f7" },
    { rgb: "236,72,153", hex: "#ec4899" },
  ];
  const accent = accents[index % accents.length];

  return (
    <motion.article
      ref={ref}
      /* ── Directional entry matching Process cards ── */
      initial={{
        opacity: 0,
        x: isEven ? -70 : 70,
        y: 40,
        rotate: isEven ? -2 : 2,
        scale: 0.96,
      }}
      animate={isInView ? { opacity: 1, x: 0, y: 0, rotate: 0, scale: 1 } : {}}
      transition={{ ...SPRING_SOFT, delay: 0.08 }}
      whileHover={{ y: -6, transition: SPRING_CRISP }}
      className="relative w-full max-w-6xl mx-auto overflow-hidden rounded-[2rem] border border-white/[0.07] bg-gradient-to-br from-white/[0.04] to-white/[0.01] backdrop-blur-xl shadow-2xl"
      style={{
        "--accent": accent.hex,
        "--accent-rgb": accent.rgb,
      }}
    >
      {/* Noise texture */}
      <div
        className="absolute inset-0 pointer-events-none rounded-[2rem] z-[1] opacity-40"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E")`,
          backgroundSize: "200px 200px",
        }}
      />

      {/* Accent glow corner */}
      <div
        className="absolute pointer-events-none z-0 w-80 h-80 rounded-full"
        style={{
          background: `radial-gradient(circle, rgba(${accent.rgb}, 0.15) 0%, transparent 70%)`,
          filter: "blur(50px)",
          top: isEven ? "-80px" : "auto",
          bottom: isEven ? "auto" : "-80px",
          left: isEven ? "-80px" : "auto",
          right: isEven ? "auto" : "-80px",
        }}
      />

      {/* Inner layout */}
      <div
        className={`relative z-[2] grid grid-cols-1 lg:grid-cols-2 gap-0 ${
          !isEven ? "lg:[direction:rtl]" : ""
        }`}
      >
        {/* ── Text side ── */}
        <div
          className={`flex flex-col justify-center gap-6 p-8 md:p-12 lg:p-14 ${
            !isEven ? "lg:[direction:ltr]" : ""
          }`}
        >
          {/* Tag pill */}
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3, ease: EASE_EXPO }}
            style={{
              fontFamily: "'Montserrat', sans-serif",
              color: accent.hex,
              background: `rgba(${accent.rgb}, 0.1)`,
              border: `1px solid rgba(${accent.rgb}, 0.25)`,
            }}
            className="inline-flex self-start text-[0.58rem] font-semibold tracking-[0.14em] uppercase px-3 py-1 rounded-full"
          >
            {card.category ?? "Project"}
          </motion.span>

          {/* Title */}
          <div className="overflow-hidden">
            <motion.h2
              initial={{ y: 50, opacity: 0 }}
              animate={isInView ? { y: 0, opacity: 1 } : {}}
              transition={{ duration: 0.85, ease: EASE_EXPO, delay: 0.2 }}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] text-white"
            >
              {card.title}
            </motion.h2>
          </div>

          {/* Accent line */}
          <motion.div
            initial={{ scaleX: 0, originX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.6, ease: EASE_EXPO, delay: 0.45 }}
            style={{
              background: `linear-gradient(90deg, ${accent.hex}, transparent)`,
            }}
            className="h-0.5 max-w-[160px] rounded-full"
          />

          {/* Micro caption */}
          {card.microCaption && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease: EASE_EXPO, delay: 0.5 }}
              style={{
                fontFamily: "'Montserrat', sans-serif",
                borderColor: `rgba(${accent.rgb}, 0.4)`,
              }}
              className="text-sm text-white/50 italic leading-relaxed border-l-2 pl-4 font-light"
            >
              {card.microCaption}
            </motion.p>
          )}

          {/* Description paragraphs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: EASE_EXPO, delay: 0.55 }}
            style={{ fontFamily: "'Montserrat', sans-serif" }}
            className="text-sm text-white/40 leading-[1.85] font-light space-y-3"
          >
            {card.description.split("\n\n").map((para, idx) => (
              <p
                key={idx}
                style={{ textAlign: "justify" }}
                className="text-justify"
              >
                {para}
              </p>
            ))}
          </motion.div>
        </div>

        {/* ── Image side ── */}
        <div
          className={`relative h-[300px] lg:h-auto min-h-[360px] overflow-hidden ${
            !isEven ? "lg:[direction:ltr]" : ""
          } lg:rounded-none rounded-b-[2rem] lg:rounded-[0] ${
            isEven ? "lg:rounded-r-[2rem]" : "lg:rounded-l-[2rem]"
          }`}
        >
          {/* Image parallax wrapper */}
          <motion.div
            ref={imgRef}
            style={{ y: imgY }}
            className="absolute inset-[-6%] w-[112%] h-[112%]"
          >
            <img
              src={card.image}
              alt={card.title}
              className="w-full h-full object-cover"
            />
          </motion.div>

          {/* Gradient overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: isEven
                ? "linear-gradient(to right, rgba(6,6,8,0.55) 0%, transparent 60%)"
                : "linear-gradient(to left, rgba(6,6,8,0.55) 0%, transparent 60%)",
            }}
          />

          {/* Hover shimmer */}
          <motion.div
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(ellipse at 50% 50%, rgba(${accent.rgb}, 0.12) 0%, transparent 70%)`,
            }}
          />
        </div>
      </div>
    </motion.article>
  );
}

/* ─── NOT FOUND ──────────────────────────────── */
function NotFound() {
  const navigate = useNavigate();
  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen flex items-center justify-center pt-32"
    >
      <div className="text-center">
        <h1
          style={{ fontFamily: "'Montserrat', sans-serif" }}
          className="text-4xl font-black mb-4 text-white"
        >
          Category not found
        </h1>
        <button
          onClick={() => navigate("/technology")}
          type="button"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
          className="text-white/50 hover:text-white text-sm underline transition-colors"
        >
          Back to Technology
        </button>
      </div>
    </motion.section>
  );
}

/* ─── ROOT ───────────────────────────────────── */
export default function CategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const category = categories.find((c) => c.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!category) return <NotFound />;

  const filteredCards = category.cardIndices.map((i) => ({
    ...workCards[i],
    originalIndex: i,
  }));

  return (
    <AnimatePresence mode="wait">
      <motion.section
        key={slug}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        className="min-h-screen pt-32 pb-28 bg-transparent relative z-10"
      >
        <div className="container mx-auto px-6">
          <CategoryHero category={category} />

          {/* Cards stack */}
          <div className="flex flex-col gap-10 md:gap-14">
            {filteredCards.map((card, i) => (
              <WorkCard key={card.originalIndex} card={card} index={i} />
            ))}
          </div>
        </div>
      </motion.section>
    </AnimatePresence>
  );
}
